/**
 * @module AdapterStatusBadge
 * @package @geenius/adapters/solidjs-kobalte
 * @description Local status badge for the SolidJS Kobalte variant.
 */

import type { AdapterStatusType } from "@geenius/adapters";
import type { JSX } from "solid-js";
import { cn } from "../lib/kobalte";

const LABELS: Record<AdapterStatusType, string> = {
  connected: "Connected",
  disconnected: "Disconnected",
  error: "Error",
  initializing: "Initializing",
};

const badgeClasses: Record<AdapterStatusType, string> = {
  connected: "border-emerald-500/30 bg-emerald-50 text-emerald-700",
  disconnected: "border-slate-300 bg-slate-100 text-slate-600",
  error: "border-red-500/30 bg-red-50 text-red-700",
  initializing: "border-amber-500/30 bg-amber-50 text-amber-700",
};

const dotClasses: Record<AdapterStatusType, string> = {
  connected: "bg-emerald-500",
  disconnected: "bg-slate-400",
  error: "bg-red-500",
  initializing: "bg-amber-500 animate-pulse motion-reduce:animate-none",
};

export interface AdapterStatusBadgeProps {
  status: AdapterStatusType;
  showLabel?: boolean;
  size?: "sm" | "md";
  class?: string;
}

export function AdapterStatusBadge(
  props: AdapterStatusBadgeProps,
): JSX.Element {
  const size = () =>
    props.size === "md"
      ? "gap-2 px-3 py-1 text-xs"
      : "gap-1.5 px-2.5 py-0.5 text-[0.625rem]";
  const dotSize = () => (props.size === "md" ? "h-2 w-2" : "h-1.5 w-1.5");

  return (
    <span
      class={cn(
        "inline-flex items-center rounded-full border font-semibold uppercase transition-colors motion-reduce:transition-none",
        size(),
        badgeClasses[props.status],
        props.class,
      )}
      data-geenius-variant="solidjs-kobalte"
    >
      <span class={cn("rounded-full", dotSize(), dotClasses[props.status])} />
      {props.showLabel !== false && <span>{LABELS[props.status]}</span>}
    </span>
  );
}
